// File: src/pages/Courses Ware/BatchTab.jsx
import React, { useEffect, useState } from "react";
import { Form, Button, Table, Spinner, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import API_BASE_URL from "../../config";
const DEBUG = true;

/* ===================== Logging helpers ===================== */
async function readBody(res) {
  const text = await res.text().catch(() => "");
  let json;
  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    json = undefined;
  }
  return { text, json };
}
function logRequest(label, { url, method, headers, body }) {
  if (!DEBUG) return;
  console.groupCollapsed(`📤 ${label} REQUEST`);
  console.log("URL:", url);
  console.log("Method:", method);
  console.log("Headers:", headers);
  if (body !== undefined) {
    try {
      console.log("Body (parsed):", JSON.parse(body));
    } catch {
      console.log("Body (text):", body);
    }
  }
  console.groupEnd();
}
async function logResponse(label, res) {
  if (!DEBUG) return { text: "", json: undefined };
  const { text, json } = await readBody(res.clone());
  console.groupCollapsed(`📥 ${label} RESPONSE`);
  console.log("ok:", res.ok, "status:", res.status, res.statusText);
  console.log("Headers:");
  res.headers.forEach((v, k) => console.log(`  ${k}: ${v}`));
  console.log("Body (text):", text);
  if (json !== undefined) console.log("Body (json):", json);
  console.groupEnd();
  return { text, json };
}

/* ===================== Date utils (robust) ===================== */
const isIsoDate = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}/.test(s);
const isSlashDate = (s) =>
  typeof s === "string" && /^\d{2}[-/]\d{2}[-/]\d{4}$/.test(s);
const isMsAjaxDate = (s) => typeof s === "string" && /\/Date\(\d+\)\//.test(s);

function toIsoFromMsAjax(s) {
  try {
    const ms = Number((s.match(/\/Date\((\d+)\)\//) || [])[1]);
    if (!Number.isFinite(ms)) return "";
    const d = new Date(ms);
    return d.toISOString();
  } catch {
    return "";
  }
}
function toIsoFromSlash(s) {
  const m = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (!m) return "";
  const [, dd, mm, yyyy] = m;
  return `${yyyy}-${mm}-${dd}T00:00:00`;
}
const toInputDate = (s) => {
  if (!s) return "";
  if (isIsoDate(s))
    return s.includes("T") ? s.split("T")[0] : s.substring(0, 10);
  if (isMsAjaxDate(s)) return toInputDate(toIsoFromMsAjax(s));
  if (isSlashDate(s)) return toInputDate(toIsoFromSlash(s));
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toISOString().substring(0, 10);
  return "";
};

/**
 * We still send an ISO-like string, but the final 00:00:00 is
 * GUARANTEED in the stored procedure by casting to DATE.
 */
const toIsoMidnight = (dateStr) =>
  dateStr ? `${dateStr}T00:00:00` : null;

/* ===================== Shape-safe normalizer ===================== */
function ci(obj) {
  const map = {};
  Object.keys(obj || {}).forEach((k) => (map[k.toLowerCase()] = obj[k]));
  return (keyCandidates) => {
    for (const k of keyCandidates) {
      const v = map[k.toLowerCase()];
      if (v !== undefined && v !== null) return v;
    }
    return undefined;
  };
}
function looksLikeDateString(x) {
  if (typeof x !== "string") return false;
  return (
    isIsoDate(x) ||
    isSlashDate(x) ||
    isMsAjaxDate(x) ||
    !isNaN(new Date(x).getTime())
  );
}
function normalizeFromArray(arr) {
  let bid = 0,
    batchName = "",
    startDate = "",
    endDate = "";
  if (!Array.isArray(arr)) return { bid, batchName, startDate, endDate };

  const idCandidate = arr.find(
    (v) => typeof v === "number" && Number.isFinite(v)
  );
  if (idCandidate !== undefined) bid = Number(idCandidate);

  const strings = arr.filter((v) => typeof v === "string");
  const dateCandidates = strings.filter(looksLikeDateString);
  if (dateCandidates[0]) startDate = dateCandidates[0];
  if (dateCandidates[1]) endDate = dateCandidates[1];

  const nameCandidate = strings.find((s) => s && !looksLikeDateString(s));
  if (nameCandidate) batchName = String(nameCandidate);

  if (!batchName && typeof arr[1] === "string") batchName = arr[1];
  if (!startDate && typeof arr[2] === "string") startDate = arr[2];
  if (!endDate && typeof arr[3] === "string") endDate = arr[3];

  return { bid, batchName, startDate, endDate };
}

function normalizeBatchDto(dto) {
  if (Array.isArray(dto)) return normalizeFromArray(dto);
  const get = ci(dto || {});
  const bid = get(["Bid", "BID", "bid", "Id", "ID"]) ?? 0;
  const batchName =
    get([
      "BatchName",
      "batchName",
      "Batch",
      "BATCH",
      "Name",
      "NAME",
      "Batch_Title",
      "Batch_Name",
      "batch",
    ]) ?? "";
  const startDate =
    get([
      "StartDate",
      "startDate",
      "Start",
      "StartDt",
      "Start_Date",
      "FromDate",
      "From",
    ]) ?? "";
  const endDate =
    get(["EndDate", "endDate", "End", "EndDt", "End_Date", "ToDate", "To"]) ??
    "";

  const programmeId =
    get(["ProgrammeId", "programmeid", "ProgrammeID", "Pid", "pid"]) ?? "";
  const programmeName =
    get(["programmeName", "ProgrammeName", "programName", "ProgramName"]) ??
    "";
  const fee = get(["fee", "Fee", "totalFee", "TotalFee"]) ?? "";
  const university =
    get(["university", "University", "uname", "Uname"]) ?? "";

  return {
    bid: Number(bid) || 0,
    batchName: String(batchName || ""),
    startDate,
    endDate,
    programmeId: programmeId !== "" ? String(programmeId) : "",
    programmeName: String(programmeName || ""),
    fee: String(fee || ""),
    university: String(university || ""),
  };
}

/* ===================== API helpers ===================== */
async function postBatchArray(payloadArray, token) {
  const url = `${API_BASE_URL}/Programme/insertBatches`;
  const body = JSON.stringify(payloadArray);
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  logRequest("insertBatches", { url, method: "POST", headers, body });
  const res = await fetch(url, { method: "POST", headers, body });
  const { text, json } = await logResponse("insertBatches", res);
  if (!res.ok)
    throw new Error(
      text ||
        (json && (json.title || json.message)) ||
        `HTTP ${res.status}`
    );
  return json ?? text;
}
async function getAllBatches(token) {
  const url = `${API_BASE_URL}/Programme/GetAllBatch`;
  console.log("🔗 GetAllBatch URL:", url);
  const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  logRequest("GetAllBatch", { url, method: "GET", headers });
  const res = await fetch(url, { headers });
  const { text, json } = await logResponse("GetAllBatch", res);
  if (!res.ok)
    throw new Error(
      text ||
        (json && (json.title || json.message)) ||
        `HTTP ${res.status}`
    );

  let data;
  try {
    data = json ?? (text ? JSON.parse(text) : []);
  } catch {
    data = [];
  }

  if (!Array.isArray(data)) {
    const wrapper = (data && (data.data || data.result || data.items)) || [];
    if (!Array.isArray(wrapper))
      throw new Error("GetAllBatch did not return an array");
    return wrapper;
  }
  return data;
}
async function getBatchById(bid, token) {
  const url = `${API_BASE_URL}/Programme/GetBatchById?Bid=${bid}`;
  const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  logRequest("GetBatchById", { url, method: "GET", headers });
  const res = await fetch(url, { headers });
  const { text, json } = await logResponse("GetBatchById", res);
  if (!res.ok)
    throw new Error(
      text ||
        (json && (json.title || json.message)) ||
        `HTTP ${res.status}`
    );
  return json ?? (text ? JSON.parse(text) : {});
}
async function deleteBatchById(bid, token) {
  const url = `${API_BASE_URL}/Programme/DeleteBatchById?Bid=${bid}`;
  const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  logRequest("DeleteBatchById(DELETE)", { url, method: "DELETE", headers });
  let res = await fetch(url, { method: "DELETE", headers });
  let out = await logResponse("DeleteBatchById(DELETE)", res);

  if (!res.ok) {
    logRequest("DeleteBatchById(GET fallback)", { url, method: "GET", headers });
    res = await fetch(url, { method: "GET", headers });
    out = await logResponse("DeleteBatchById(GET fallback)", res);
    if (!res.ok)
      throw new Error(
        out.text ||
          (out.json && (out.json.title || out.json.message)) ||
          `HTTP ${res.status}`
      );
  }
  return out.json ?? out.text;
}

/* ===================== Component ===================== */
const BatchTab = () => {
  const [form, setForm] = useState({
    batchName: "",
    startDate: "",
    endDate: "",
    bid: 0,
    programmeId: "", // or "ALL" for all courses
    classId: "",
  });
  const [saving, setSaving] = useState(false);

  const [batches, setBatches] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState("");

  const isEditMode = Number(form.bid) > 0;

  useEffect(() => {
    refreshList();
    fetchBoards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBoards = async () => {
    try {
      const token = localStorage.getItem("jwt");
      const res = await fetch(`${API_BASE_URL}/Group/GetCoursesByBatch`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const normalized = Array.isArray(data)
        ? data.map((b) => ({
            programmeId:
              b.programmeId != null ? String(b.programmeId) : "",
            programmeCode: b.programmeCode,
            programmeName: b.programmeName,
          }))
        : [];
      console.log("📋 Boards loaded:", normalized);
      setBoards(normalized);
    } catch {
      toast.error("❌ Failed to load courses");
    }
  };

  async function refreshList() {
    setLoadingList(true);
    setListError("");
    try {
      const token = localStorage.getItem("jwt");
      const rows = await getAllBatches(token);
      const normalized = rows.map(normalizeBatchDto).map((r, i) => {
        if (DEBUG && (!r.batchName || r.batchName === "")) {
          console.warn(
            "⚠️ Row missing batchName, raw:",
            rows[i],
            "normalized:",
            r
          );
        }
        return r;
      });
      if (DEBUG) console.log("✅ Normalized batches:", normalized);
      setBatches(normalized);
    } catch (err) {
      setListError(err.message || "Failed to load batches.");
      if (DEBUG) console.error("❌ getAllBatches:", err);
    } finally {
      setLoadingList(false);
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (DEBUG) console.log(`✏️ handleChange ${name}=`, value);
    setForm((p) => ({
      ...p,
      [name]: value,
    }));
  };

  const handleBoardChange = (e) => {
    const programmeId = e.target.value;
    if (programmeId) {
      console.log("🔄 Course changed:", { programmeId });
      setForm((prev) => ({
        ...prev,
        programmeId,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        programmeId: "",
      }));
    }
  };

  const handleEdit = (row) => {
    const programmeId = row.programmeId ? String(row.programmeId) : "";
    setForm({
      batchName: row.batchName || "",
      startDate: toInputDate(row.startDate),
      endDate: toInputDate(row.endDate),
      bid: Number(row.bid) || 0,
      programmeId,
      classId: "",
    });
    toast.info("✏️ Edit mode");
  };

  const handleCancelEdit = () => {
    setForm({
      batchName: "",
      startDate: "",
      endDate: "",
      bid: 0,
      programmeId: "",
      classId: "",
    });
    toast.dismiss();
  };

  const handleDelete = async (row) => {
    if (
      !window.confirm(
        `Delete batch "${row.batchName || "(no name)"}" (Bid=${row.bid})?`
      )
    )
      return;
    try {
      const token = localStorage.getItem("jwt");
      await deleteBatchById(row.bid, token);
      toast.success("🗑️ Deleted");
      await refreshList();
      if (isEditMode && Number(form.bid) === Number(row.bid))
        handleCancelEdit();
    } catch (err) {
      console.error("❌ Delete failed:", err);
      toast.error(`❌ Delete failed: ${err.message}`);
    }
  };

const handleSave = async () => {
  const { batchName, startDate, endDate, bid, programmeId } = form;

  if (DEBUG) console.log("🧾 handleSave form:", form);

  const trimmedBatchName = (batchName || "").trim();

  if (!trimmedBatchName || !startDate || !endDate || !programmeId) {
    toast.error(
      "Please fill all required fields: Batch, Start Date, End Date, and Course."
    );
    return;
  }
  if (new Date(startDate) > new Date(endDate)) {
    toast.error("Start Date cannot be after End Date");
    return;
  }

  const startIso = toIsoMidnight(startDate);
  const endIso = toIsoMidnight(endDate);

  let payload = [];

  if (programmeId === "ALL") {
    // 🔹 Save for ALL courses
    if (!boards || boards.length === 0) {
      toast.error("No courses available to save for ALL.");
      return;
    }
    payload = boards.map((b) => ({
      Bid: 0, // always insert new for ALL
      Batch: trimmedBatchName,          // IMPORTANT: matches backend 'Batch'
      BatchName: trimmedBatchName,      // optional, harmless
      StartDate: startIso,
      EndDate: endIso,
      Pid: Number(b.programmeId),
      Fee: 0,                           // IMPORTANT: decimal, not null
      university: null,                 // we don't use it now
    }));
    console.log("📦 Payload for ALL courses:", payload);
  } else {
    // 🔹 Single course
    const dto = {
      Bid: Number(bid) || 0,
      Batch: trimmedBatchName,          // IMPORTANT
      BatchName: trimmedBatchName,      // optional
      StartDate: startIso,
      EndDate: endIso,
      Pid: Number(programmeId),
      Fee: 0,                           // IMPORTANT
      university: null,                 // not used
    };
    payload = [dto];
    console.log("📦 Payload for single course:", payload);
  }

  try {
    setSaving(true);
    const token = localStorage.getItem("jwt");
    await postBatchArray(payload, token);
    toast.success(
      isEditMode
        ? "✅ Batch updated successfully"
        : "✅ Batch created successfully"
    );
    await refreshList();
    setForm({
      batchName: "",
      startDate: "",
      endDate: "",
      bid: 0,
      programmeId: "",
      classId: "",
    });
  } catch (err) {
    console.error("❌ Save failed:", err);
    toast.error(`❌ Save failed: ${err.message}`);
  } finally {
    setSaving(false);
  }
};


  return (
    <div className="container py-0 pt-0 welcome-card animate-welcome">
      {/* Form Card */}
      <div className="mb-0 bg-glass p-3 rounded">
        <h5 className="mb-3 text-primary">Add / Edit Batch</h5>
        <Form onSubmit={(e) => e.preventDefault()}>
          <div className="row g-3">
            {/* Select Course */}
            <div className="col-12 col-md-6 col-lg-4">
              <Form.Group>
                <Form.Label>Select Course</Form.Label>
                <Form.Control
                  as="select"
                  name="programmeId"
                  value={form.programmeId}
                  onChange={handleBoardChange}
                  required
                >
                  <option value="">Select Course</option>
                  <option value="ALL">All Courses</option>
                  {boards.map((board) => (
                    <option
                      key={board.programmeId}
                      value={board.programmeId}
                    >
                      {board.programmeCode} - {board.programmeName}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </div>

            {/* Batch Name */}
            <div className="col-12 col-md-6 col-lg-4">
              <Form.Group>
                <Form.Label>Batch</Form.Label>
                <Form.Control
                  name="batchName"
                  value={form.batchName}
                  onChange={handleChange}
                  placeholder="e.g., 2024(1)"
                  required
                />
              </Form.Group>
            </div>

            {/* Start Date */}
            <div className="col-12 col-md-6 col-lg-4">
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  name="startDate"
                  value={form.startDate}
                  onChange={handleChange}
                />
              </Form.Group>
            </div>

            {/* End Date */}
            <div className="col-12 col-md-6 col-lg-4">
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  name="endDate"
                  value={form.endDate}
                  onChange={handleChange}
                />
              </Form.Group>
            </div>

            {/* Actions (Save / Cancel) */}
            <div className="col-12 col-md-6 col-lg-4 d-flex align-items-end">
              <div className="d-grid d-lg-flex gap-2 w-100">
                <Button
                  className="rounded-pill px-4"
                  variant={isEditMode ? "warning" : "success"}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSave();
                  }}
                  disabled={saving}
                >
                  {saving
                    ? isEditMode
                      ? "Updating..."
                      : "Saving..."
                    : isEditMode
                    ? "Update"
                    : "Save"}
                </Button>

                {isEditMode && (
                  <Button
                    variant="outline-secondary"
                    className="px-4"
                    onClick={handleCancelEdit}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Form>
      </div>

      {/* List Card */}
      <div className="p-4 mt-3 rounded bg-white border shadow-sm">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">📋 Batches</h5>
        </div>

        {listError && (
          <Alert variant="warning" className="mb-3">
            Couldn’t load from server: {listError}
          </Alert>
        )}

        <div className="p-0 overflow-auto custom-scrollbar flex-grow-1">
          {loadingList ? (
            <div className="py-3 text-center">
              <Spinner animation="border" role="status" />
            </div>
          ) : batches.length === 0 ? (
            <p className="mb-0">No batches found.</p>
          ) : (
            <div className="batch-table-scroll">
              <Table bordered hover responsive size="sm" className="mb-0">
                <thead>
                  <tr className="bg-light">
                    <th style={{ width: 200 }}>Programme</th>
                    <th style={{ width: 150 }}>Batch</th>
                    <th style={{ width: 120 }}>Start Date</th>
                    <th style={{ width: 120 }}>End Date</th>
                    {/* Fee & University are intentionally hidden now */}
                    <th style={{ width: 120 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b, idx) => {
                    const start = toInputDate(b.startDate);
                    const end = toInputDate(b.endDate);
                    return (
                      <tr key={b.bid || idx}>
                        <td data-label="Programme" className="text-start">
                          {b.programmeName || <em>(no programme)</em>}
                        </td>
                        <td data-label="Batch" className="text-start">
                          {b.batchName || <em>(no name)</em>}
                        </td>
                        <td data-label="Start Date">{start}</td>
                        <td data-label="End Date">{end}</td>
                        <td
                          data-label="Actions"
                          className="d-flex gap-2"
                          style={{
                            margin: "auto",
                            alignItems: "center",
                            textAlign: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            size="sm"
                            variant="outline-info"
                            onClick={() => handleEdit(b)}
                            type="button"
                            style={{
                              marginTop: "0px",
                              marginBottom: "0px",
                            }}
                          >
                            <i className="fa-solid fa-pen-to-square"></i>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDelete(b)}
                            type="button"
                            style={{
                              marginTop: "0px",
                              marginBottom: "0px",
                            }}
                          >
                            <i className="fa-solid fa-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BatchTab;
